/**
 * Plan by date — "if I buy on day X, which card gives me longest to pay?"
 *
 * The old page ranked cards for a purchase made *today*. Planning needs any
 * day: the big purchase can often wait a week for a card to reset, and that
 * week can be worth a month of interest-free time. So:
 *
 *   * **The timeline** draws every card's cycles across the next eight weeks —
 *     each bar one cycle, the gaps between bars the resets, diamonds the bills.
 *     A gold diamond is a due date the issuer printed; an outlined one is
 *     projected (§2.7).
 *   * **A "buy on" cursor** can be dragged (or clicked, or moved with the arrow
 *     keys) to any day. Every lane lights the cycle that day lands in and draws
 *     a bracket to the bill it will be on — buy here, pay there.
 *   * **The ribbon on top** colours each day by the card that would give the
 *     longest float, so the good windows show without dragging at all.
 *   * **The ranking** re-sorts, animated, for the chosen day.
 *
 * It ranks interest-free days only — rewards are not in it, and the caveat
 * travels with it (server-supplied, so a redesign can't drop it).
 */

import { LayoutGroup, motion, useReducedMotion } from "framer-motion";
import { CalendarClock, Info } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import {
  addDays,
  cardName,
  daysBetween,
  floatsOn,
  type FloatOn,
} from "@/components/cycles/cycleMath";
import { CardSwatch, Panel } from "@/components/ui/primitives";
import type { CycleCard, CyclePlan } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

const BACK_DAYS = 6;
const AHEAD_DAYS = 49;

export function PurchasePlanner({
  cards,
  today,
  caveat,
}: {
  cards: CycleCard[];
  today: string;
  caveat: string;
}) {
  const reduce = useReducedMotion();
  const windowStart = addDays(today, -BACK_DAYS);
  const windowEnd = addDays(today, AHEAD_DAYS);
  const total = daysBetween(windowStart, windowEnd) + 1;
  const xOf = (iso: string) => (daysBetween(windowStart, iso) / total) * 100;
  const lastPlannable = addDays(today, AHEAD_DAYS - 7);

  const [buyOn, setBuyOn] = React.useState(today);
  const ranked = React.useMemo(() => floatsOn(cards, buyOn), [cards, buyOn]);
  const best = ranked[0];

  // The best card for every day in the window — the ribbon.
  const ribbon = React.useMemo(() => {
    const runs: { from: string; to: string; card: CycleCard; days: number }[] = [];
    for (let i = 0; i <= daysBetween(today, lastPlannable); i++) {
      const d = addDays(today, i);
      const top = floatsOn(cards, d)[0];
      if (!top) continue;
      const last = runs[runs.length - 1];
      if (last && last.card.card_id === top.card.card_id && addDays(last.to, 1) === d) {
        last.to = d;
      } else {
        runs.push({ from: d, to: d, card: top.card, days: top.days });
      }
    }
    return runs;
  }, [cards, today, lastPlannable]);

  // The single best day in the window, and what buying today gets instead.
  const bestToday = React.useMemo(() => floatsOn(cards, today)[0], [cards, today]);
  const bestDay = React.useMemo(() => {
    let top: { date: string; hit: FloatOn } | null = null;
    for (let i = 0; i <= daysBetween(today, lastPlannable); i++) {
      const d = addDays(today, i);
      const hit = floatsOn(cards, d)[0];
      if (hit && (!top || hit.days > top.hit.days)) top = { date: d, hit };
    }
    return top;
  }, [cards, today, lastPlannable]);

  // Quick picks: today, and the day after each of the next few resets.
  const picks = React.useMemo(() => {
    const out: { date: string; label: string }[] = [{ date: today, label: "Today" }];
    const resets = cards
      .filter((c) => c.cycle_end)
      .map((c) => ({ date: addDays(c.cycle_end!, 1), card: c }))
      .filter((r) => r.date <= lastPlannable)
      .sort((a, b) => (a.date < b.date ? -1 : 1));
    const seen = new Set<string>();
    for (const r of resets) {
      if (seen.has(r.date) || r.date === today) continue;
      seen.add(r.date);
      out.push({ date: r.date, label: `${formatDate(r.date).slice(0, 6)} · ${r.card.last4} resets` });
      if (out.length >= 4) break;
    }
    return out;
  }, [cards, today, lastPlannable]);

  // --- dragging the cursor -------------------------------------------------
  const trackRef = React.useRef<HTMLDivElement>(null);
  const dragging = React.useRef(false);
  const dayAt = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return today;
    const rect = el.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const d = addDays(windowStart, Math.floor(frac * total));
    return d < today ? today : d > lastPlannable ? lastPlannable : d;
  };
  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setBuyOn(dayAt(e.clientX));
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (dragging.current) setBuyOn(dayAt(e.clientX));
  };
  const stopDrag = () => {
    dragging.current = false;
  };
  const step = (delta: number) =>
    setBuyOn((d) => {
      const next = addDays(d, delta);
      return next < today ? today : next > lastPlannable ? lastPlannable : next;
    });

  const cursorAt = xOf(buyOn);
  const glide = reduce ? "none" : "left 0.35s cubic-bezier(0.16,1,0.3,1), width 0.35s cubic-bezier(0.16,1,0.3,1)";

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-medium tracking-[-0.02em] text-ink">
            Plan a purchase <em className="accent-serif">date</em>
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Drag the cursor to the day you'd buy. Each lane shows the cycle that day lands in and
            the bill it ends up on; the ranking re-sorts by days to pay.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {picks.map((p) => (
            <button
              key={p.date}
              type="button"
              onClick={() => setBuyOn(p.date)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[12px] transition-colors",
                buyOn === p.date
                  ? "border-brass/50 bg-brass/[0.12] text-ink"
                  : "border-hairline text-muted hover:text-ink",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-3.5 xl:grid-cols-[minmax(0,1fr)_20rem]">
        {/* --- timeline ------------------------------------------------------ */}
        <Panel className="relative animate-rise overflow-hidden p-4 sm:p-5" style={{ animationDelay: "120ms" }}>
          <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 sm:grid-cols-[9rem_minmax(0,1fr)]">
            {/* Month/week header */}
            <div />
            <WeekHeader start={windowStart} total={total} xOf={xOf} />

            {/* Ribbon: best card per day */}
            <div className="flex items-center text-[11px] text-faint">Longest float</div>
            <div className="relative my-2 h-5 overflow-hidden rounded-md bg-raised/60">
              {ribbon.map((run, i) => {
                const left = xOf(run.from);
                const width = xOf(addDays(run.to, 1)) - left;
                return (
                  <button
                    key={`${run.from}-${run.card.card_id}`}
                    type="button"
                    onClick={() => setBuyOn(run.from)}
                    title={`${formatDate(run.from)}${run.to !== run.from ? ` – ${formatDate(run.to)}` : ""}: ${cardName(run.card)} gives the longest float`}
                    className="absolute inset-y-0 flex items-center justify-center overflow-hidden border-r border-panel font-mono text-[9.5px] tracking-[0.1em]"
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      backgroundImage: run.card.theme
                        ? `linear-gradient(90deg, ${run.card.theme.gradient_from}, ${run.card.theme.gradient_to})`
                        : undefined,
                      color: run.card.theme?.foreground,
                      opacity: reduce ? 1 : undefined,
                      animation: reduce ? undefined : `rise 0.6s cubic-bezier(0.16,1,0.3,1) ${0.3 + i * 0.05}s both`,
                    }}
                  >
                    {width > 7 ? run.card.last4 : ""}
                  </button>
                );
              })}
            </div>

            {/* Lanes */}
            {cards.map((card, i) => {
              const hit = ranked.find((r) => r.card.card_id === card.card_id);
              return (
                <React.Fragment key={card.card_id}>
                  <Link
                    to={`/cards/${card.card_id}`}
                    className="flex min-w-0 items-center gap-2 py-1.5 text-[12.5px] text-muted hover:text-ink"
                    title={cardName(card)}
                  >
                    <CardSwatch theme={card.theme} />
                    <span className="hidden truncate sm:inline">{cardName(card)}</span>
                    <span className="font-mono text-[10.5px] text-faint sm:hidden">{card.last4}</span>
                  </Link>
                  <Lane
                    card={card}
                    hit={hit ?? null}
                    windowStart={windowStart}
                    windowEnd={windowEnd}
                    xOf={xOf}
                    index={i}
                    glide={glide}
                    isBest={best?.card.card_id === card.card_id}
                  />
                </React.Fragment>
              );
            })}

            {/* The draggable layer spans the whole track column. */}
            <div />
            <div className="relative mt-2 h-8">
              <div
                ref={trackRef}
                role="slider"
                tabIndex={0}
                aria-label="Purchase date"
                aria-valuemin={0}
                aria-valuemax={daysBetween(today, lastPlannable)}
                aria-valuenow={daysBetween(today, buyOn)}
                aria-valuetext={formatDate(buyOn)}
                onKeyDown={(e) => {
                  const moves: Partial<Record<string, () => void>> = {
                    ArrowRight: () => step(1),
                    ArrowLeft: () => step(-1),
                    PageUp: () => step(7),
                    PageDown: () => step(-7),
                    Home: () => setBuyOn(today),
                  };
                  const move = moves[e.key];
                  if (move) {
                    e.preventDefault();
                    move();
                  }
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={stopDrag}
                onPointerCancel={stopDrag}
                className="absolute inset-0 cursor-ew-resize touch-none rounded-lg border border-dashed border-hairline-strong/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50"
              >
                <span
                  className="pointer-events-none absolute inset-y-0 left-0 rounded-l-lg bg-raised/70"
                  style={{ width: `${xOf(today)}%` }}
                />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[11px] text-faint">
                  drag · click · arrow keys
                </span>
                <span
                  className="pointer-events-none absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-ink px-2 py-0.5 text-[11px] font-medium text-[rgb(var(--surface-base))] shadow-lg"
                  style={{ left: `${cursorAt + 100 / total / 2}%`, transition: glide }}
                >
                  Buy {buyOn === today ? "today" : formatDate(buyOn).slice(0, 6)}
                </span>
              </div>
            </div>
          </div>

          {/* The cursor line, drawn over header, ribbon and lanes. */}
          <CursorLine trackRef={trackRef} at={cursorAt + 100 / total / 2} glide={glide} todayAt={xOf(today) + 100 / total / 2} />

          <Insight
            buyOn={buyOn}
            today={today}
            best={best ?? null}
            bestToday={bestToday ?? null}
            bestDay={bestDay}
            onJump={setBuyOn}
          />

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-faint">
            <Legend swatch={<span className="inline-block h-2.5 w-5 rounded-sm bg-ink/50" />}>a cycle</Legend>
            <Legend swatch={<span className="inline-block h-2.5 w-1 rounded-sm bg-hairline-strong" />}>gap = reset</Legend>
            <Legend swatch={<span className="inline-block h-2.5 w-2.5 rotate-45 bg-brass" />}>bill due (printed)</Legend>
            <Legend swatch={<span className="inline-block h-2.5 w-2.5 rotate-45 border border-dashed border-ink/60" />}>bill due (projected)</Legend>
          </div>
        </Panel>

        {/* --- ranking ------------------------------------------------------- */}
        <Panel className="animate-rise flex flex-col overflow-hidden" style={{ animationDelay: "180ms" }}>
          <div className="border-b border-hairline px-5 py-4">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-faint" aria-hidden />
              <span className="label-micro">Buying on {formatDate(buyOn)}</span>
            </div>
            {best && (
              <p key={best.card.card_id + buyOn} className="animate-rise mt-2 text-[13px] text-muted" style={{ animationDuration: "0.4s" }}>
                <span className="text-ink">{cardName(best.card)}</span> gives the longest —{" "}
                pay by <span className="text-ink">{formatDate(best.due)}</span>.
              </p>
            )}
          </div>
          <LayoutGroup>
            <ol className="flex-1 divide-y divide-hairline">
              {ranked.map((r, i) => (
                <RankRow key={r.card.card_id} r={r} i={i} longest={ranked[0]?.days ?? 1} reduce={!!reduce} />
              ))}
            </ol>
          </LayoutGroup>
          <p className="flex items-start gap-2 border-t border-hairline bg-raised/40 px-5 py-3 text-[11.5px] text-muted">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
            {caveat}
          </p>
        </Panel>
      </div>
    </section>
  );
}

/**
 * The planning takeaway in one sentence: what waiting until the chosen day
 * gains (or costs) against buying today, or — on today — the best day ahead.
 */
function Insight({
  buyOn,
  today,
  best,
  bestToday,
  bestDay,
  onJump,
}: {
  buyOn: string;
  today: string;
  best: FloatOn | null;
  bestToday: FloatOn | null;
  bestDay: { date: string; hit: FloatOn } | null;
  onJump: (d: string) => void;
}) {
  if (!best || !bestToday) return null;
  let body: React.ReactNode;
  if (buyOn === today) {
    if (!bestDay || bestDay.hit.days <= bestToday.days) {
      body = (
        <>
          Today is as good as it gets for the next six weeks:{" "}
          <span className="text-ink">{cardName(bestToday.card)}</span> gives{" "}
          <span className="text-ink">{bestToday.days} days</span> to pay.
        </>
      );
    } else {
      body = (
        <>
          The longest float ahead is <span className="text-ink">{bestDay.hit.days} days</span> —
          buy on <span className="text-ink">{formatDate(bestDay.date).slice(0, 6)}</span> with{" "}
          <span className="text-ink">{cardName(bestDay.hit.card)}</span>, {bestDay.hit.days - bestToday.days}{" "}
          more than the best card today.{" "}
          <button
            type="button"
            onClick={() => onJump(bestDay.date)}
            className="text-brass underline-offset-2 hover:underline"
          >
            Show that day
          </button>
        </>
      );
    }
  } else {
    const wait = daysBetween(today, buyOn);
    const diff = best.days - bestToday.days;
    body = (
      <>
        Waiting {wait} day{wait === 1 ? "" : "s"} gives{" "}
        <span className="text-ink">{best.days} days</span> on{" "}
        <span className="text-ink">{cardName(best.card)}</span> —{" "}
        {diff === 0 ? (
          "the same as the best card today"
        ) : (
          <span className={diff > 0 ? "text-paid" : "text-warn"}>
            {Math.abs(diff)} {diff > 0 ? "more" : "fewer"} days to pay
          </span>
        )}{" "}
        than buying today ({cardName(bestToday.card)}, {bestToday.days}d).
      </>
    );
  }
  return (
    <p
      key={buyOn}
      className="animate-rise mt-5 rounded-xl border border-hairline bg-raised/50 px-4 py-3 text-[13px] leading-relaxed text-muted"
      style={{ animationDuration: "0.45s" }}
    >
      {body}
    </p>
  );
}

function RankRow({ r, i, longest, reduce }: { r: FloatOn; i: number; longest: number; reduce: boolean }) {
  const projected = !r.plan.due_printed;
  return (
    <motion.li
      layout={reduce ? false : "position"}
      transition={{ type: "spring", stiffness: 380, damping: 34 }}
      className="flex items-center gap-3 bg-panel px-5 py-2"
    >
      <span className="w-4 shrink-0 text-right text-xs tabular-nums text-faint">{i + 1}</span>
      <CardSwatch theme={r.card.theme} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] text-ink">{cardName(r.card)}</div>
        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-raised">
          <div
            className={cn("h-full rounded-full", i === 0 ? "bg-brass" : "bg-ink/25")}
            style={{
              width: `${Math.max(4, (r.days / Math.max(1, longest)) * 100)}%`,
              transition: reduce ? "none" : "width 0.5s cubic-bezier(0.16,1,0.3,1)",
            }}
          />
        </div>
        <div className="mt-0.5 text-[11px] text-faint">
          pay by {projected ? "~" : ""}
          {formatDate(r.due).slice(0, 6)}
        </div>
      </div>
      <span
        className={cn(
          "w-12 shrink-0 text-right font-display text-lg font-medium tabular-nums",
          i === 0 ? "text-brass" : "text-ink",
        )}
        title={r.card.due_basis}
      >
        {r.days}d
      </span>
    </motion.li>
  );
}

function Lane({
  card,
  hit,
  windowStart,
  windowEnd,
  xOf,
  index,
  glide,
  isBest,
}: {
  card: CycleCard;
  hit: FloatOn | null;
  windowStart: string;
  windowEnd: string;
  xOf: (iso: string) => number;
  index: number;
  glide: string;
  isBest: boolean;
}) {
  const reduce = useReducedMotion();
  const theme = card.theme;
  const fill = theme
    ? `linear-gradient(90deg, ${theme.gradient_from}, ${theme.gradient_to})`
    : "rgb(var(--border-strong))";

  const visible = (p: CyclePlan) => p.end >= windowStart && p.start <= windowEnd;

  return (
    <div className="relative h-10">
      {card.path.filter(visible).map((p) => {
        const from = p.start < windowStart ? windowStart : p.start;
        const to = p.end > windowEnd ? windowEnd : p.end;
        const left = xOf(from);
        const width = xOf(addDays(to, 1)) - left;
        const isHit = hit?.plan.start === p.start;
        const isOpen = card.cycle_start === p.start;
        return (
          <div
            key={p.start}
            className={cn(
              "absolute top-[0.85rem] h-3.5 rounded-[5px]",
              isHit && "ring-2 ring-brass/80 ring-offset-1 ring-offset-[rgb(var(--surface-panel))]",
            )}
            title={`${formatDate(p.start)} – ${formatDate(p.end)}${p.billed ? " · billed" : " · projected"}`}
            style={{
              left: `calc(${left}% + 1px)`,
              width: `calc(${width}% - 2px)`,
              backgroundImage: fill,
              opacity: p.billed ? 0.3 : isOpen || isHit ? 0.95 : 0.5,
              transformOrigin: "left",
              animation: reduce ? undefined : `lane-grow 0.8s cubic-bezier(0.16,1,0.3,1) ${0.15 + index * 0.06}s both`,
              transition: "opacity 0.3s",
            }}
          />
        );
      })}

      {/* Bills: printed gold, projected outlined. */}
      {card.path
        .filter((p) => p.due_on && p.due_on >= windowStart && p.due_on <= windowEnd)
        .map((p) => (
          <span
            key={`due-${p.start}`}
            className={cn(
              "absolute top-[0.93rem] z-[2] h-2.5 w-2.5 -translate-x-1/2 rotate-45",
              p.due_printed ? "bg-brass" : "border border-dashed border-ink/70 bg-panel",
            )}
            style={{ left: `${xOf(p.due_on!) + (100 / (daysBetween(windowStart, windowEnd) + 1)) / 2}%` }}
            title={`Bill for ${formatDate(p.start)} – ${formatDate(p.end)} due ${formatDate(p.due_on)}${p.due_printed ? " (printed)" : " (projected)"}`}
          />
        ))}

      {/* Buy here → pay there. */}
      {hit && <Bracket hit={hit} xOf={xOf} glide={glide} isBest={isBest} />}
    </div>
  );
}

/** A thin bracket over the lane from the purchase day to the bill it lands on. */
function Bracket({
  hit,
  xOf,
  glide,
  isBest,
}: {
  hit: FloatOn;
  xOf: (iso: string) => number;
  glide: string;
  isBest: boolean;
}) {
  // The purchase day is the plan-relative start of the float: due − days.
  const buy = addDays(hit.due, -hit.days);
  const half = xOf(addDays(buy, 1)) - xOf(buy);
  const left = xOf(buy) + half / 2;
  const right = Math.min(100, xOf(hit.due) + half / 2);
  return (
    <div
      className={cn(
        "pointer-events-none absolute top-[0.3rem] z-[3] h-[0.45rem] rounded-t-[4px] border-x border-t",
        isBest ? "border-brass" : "border-ink/30",
      )}
      style={{ left: `${left}%`, width: `${Math.max(0, right - left)}%`, transition: glide }}
    >
      {right - left > 9 && (
        <span
          className={cn(
            "absolute -top-[0.55rem] left-1/2 -translate-x-1/2 rounded bg-panel px-1 font-mono text-[9.5px] tabular-nums",
            isBest ? "text-brass" : "text-faint",
          )}
        >
          {hit.days}d
        </span>
      )}
    </div>
  );
}

function WeekHeader({ start, total, xOf }: { start: string; total: number; xOf: (iso: string) => number }) {
  const ticks: { iso: string; label: string; month: boolean }[] = [];
  for (let i = 0; i < total; i++) {
    const iso = addDays(start, i);
    const day = Number(iso.slice(8, 10));
    if (day === 1) ticks.push({ iso, label: formatDate(iso).slice(3, 6), month: true });
    // A week tick within two days of a month label would print over it.
    else if (i % 7 === 0 && day > 3 && day < 30) ticks.push({ iso, label: String(day), month: false });
  }
  return (
    <div className="relative h-5 border-b border-hairline">
      {ticks.map((t) => (
        <span
          key={t.iso}
          className={cn(
            "absolute bottom-1 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.08em]",
            // Week ticks crowd a phone-width track; months alone read fine there.
            t.month ? "text-ink" : "hidden text-faint sm:inline",
          )}
          style={{ left: `${xOf(t.iso) + 100 / total / 2}%` }}
        >
          {t.label}
        </span>
      ))}
    </div>
  );
}

/**
 * The vertical cursor, measured against the track so it lines up with the lane
 * column whatever the label column's width.
 */
function CursorLine({
  trackRef,
  at,
  glide,
  todayAt,
}: {
  trackRef: React.RefObject<HTMLDivElement>;
  at: number;
  glide: string;
  todayAt: number;
}) {
  const [box, setBox] = React.useState<{ left: number; width: number; top: number; height: number } | null>(null);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const measure = () => {
      const track = trackRef.current;
      const host = ref.current?.parentElement;
      if (!track || !host) return;
      const t = track.getBoundingClientRect();
      const h = host.getBoundingClientRect();
      setBox({ left: t.left - h.left, width: t.width, top: 16, height: t.bottom - h.top - 16 });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    if (ref.current?.parentElement) ro.observe(ref.current.parentElement);
    return () => ro.disconnect();
  }, [trackRef]);

  return (
    <div ref={ref} aria-hidden className="pointer-events-none">
      {box && (
        <>
          <span
            className="absolute w-px bg-ink/25"
            style={{ left: box.left + (box.width * todayAt) / 100, top: box.top, height: box.height }}
          />
          <span
            className="absolute z-[4] w-0.5 -translate-x-1/2 rounded bg-brass shadow-[0_0_10px_rgb(var(--brass)/0.6)]"
            style={{
              left: box.left + (box.width * at) / 100,
              top: box.top,
              height: box.height - 32,
              transition: glide.split(",")[0],
            }}
          />
        </>
      )}
    </div>
  );
}

function Legend({ swatch, children }: { swatch: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {swatch}
      {children}
    </span>
  );
}
